import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PartnerContractPartyRequest } from '@interfaces/partners/contracts/requests/partner-contract.request';
import { PartnerContractPartyType } from '@interfaces/partners/contracts/responses/partner-contract.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';

/** Percentuais padrão do Anexo I (regra geral e crédito já aprovado), os mesmos do backend. */
export const DEFAULT_COMMISSION_GENERAL_RATE = 10;
export const DEFAULT_COMMISSION_APPROVED_CREDIT_RATE = 20;

/** Valores iniciais dos dados da parte (o tipo só é escolhido na criação). */
export type PartnerContractPartyDefaults = Partial<PartnerContractPartyRequest> & {
  partyType?: PartnerContractPartyType;
};

/**
 * Percentual inteiro de 0 a 100 (o backend recusa fração e fora da faixa). Não é obrigatório: vazio
 * volta ao padrão (10 e 20) ao gravar, e a tela diz isso sob o campo (revisão do card 3643).
 */
const PERCENT_VALIDATORS: ValidatorFn[] = [
  Validators.min(0),
  Validators.max(100),
  Validators.pattern(/^\d+$/),
];

/** Documento só com dígitos e no tamanho certo (11 = CPF, 14 = CNPJ), quando informado. */
function documentLength(length: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const digits = String(control.value ?? '').replace(/\D/g, '');
    return digits && digits.length !== length ? { documentLength: { length } } : null;
  };
}

/**
 * Formulário dos dados da parte, compartilhado pela modal "Gerar contrato" e pelo painel lateral
 * do editor. Só razão social (ou nome completo) e data são obrigatórias, mais os dois percentuais
 * do Anexo I: o que ficar vazio continua no texto como `{{chave}}`, e a tela avisa.
 *
 * O signatário da Legatus NÃO é campo: vem da Parametrização ao gerar (só leitura na tela).
 */
export function createPartnerContractPartyForm(
  fb: FormBuilder,
  defaults: PartnerContractPartyDefaults = {}
): FormGroup {
  const value = partnerContractPartyFormValue(defaults);
  return fb.group({
    partyType: [value.partyType, [Validators.required]],
    partyLegalName: [value.partyLegalName, [Validators.required, Validators.maxLength(300)]],
    partyShortName: [value.partyShortName, [Validators.maxLength(150)]],
    partyCnpj: [value.partyCnpj, [documentLength(14)]],
    partyCpf: [value.partyCpf, [documentLength(11)]],
    partyAddress: [value.partyAddress, [Validators.maxLength(500)]],
    partyRepresentativeName: [value.partyRepresentativeName, [Validators.maxLength(200)]],
    partyRepresentativeEmail: [
      value.partyRepresentativeEmail,
      [Validators.email, Validators.maxLength(256)],
    ],
    contractDate: [value.contractDate, [Validators.required]],
    commissionGeneralRate: [value.commissionGeneralRate, PERCENT_VALIDATORS],
    commissionApprovedCreditRate: [value.commissionApprovedCreditRate, PERCENT_VALIDATORS],
  });
}

/**
 * Valor completo do formulário a partir dos padrões, para `reset`/`patchValue` sem recriar o
 * `FormGroup` (os `app-input` ficam presos ao grupo com que nasceram).
 */
export function partnerContractPartyFormValue(defaults: PartnerContractPartyDefaults = {}) {
  return {
    partyType: defaults.partyType ?? ('Company' as PartnerContractPartyType),
    partyLegalName: defaults.partyLegalName ?? '',
    partyShortName: defaults.partyShortName ?? '',
    partyCnpj: defaults.partyCnpj ?? '',
    partyCpf: defaults.partyCpf ?? '',
    partyAddress: defaults.partyAddress ?? '',
    partyRepresentativeName: defaults.partyRepresentativeName ?? '',
    partyRepresentativeEmail: defaults.partyRepresentativeEmail ?? '',
    contractDate: defaults.contractDate ?? null,
    commissionGeneralRate: defaults.commissionGeneralRate ?? DEFAULT_COMMISSION_GENERAL_RATE,
    commissionApprovedCreditRate:
      defaults.commissionApprovedCreditRate ?? DEFAULT_COMMISSION_APPROVED_CREDIT_RATE,
  };
}

/** A parte é pessoa física (CPF, "Nome completo"). */
export function isIndividualParty(form: FormGroup): boolean {
  return form.get('partyType')?.value === 'Individual';
}

/** Rótulo de cada campo, como aparece na tela, para explicar o que falta corrigir. */
function partyFieldLabel(name: string, individual: boolean): string {
  const labels: Record<string, string> = {
    partyType: 'Tipo da parte',
    partyLegalName: individual ? 'Nome completo' : 'Razão social da parceira',
    partyShortName: 'Nome curto',
    partyCnpj: 'CNPJ',
    partyCpf: 'CPF',
    partyAddress: individual ? 'Endereço' : 'Endereço da sede',
    partyRepresentativeName: 'Representante da parceira',
    partyRepresentativeEmail: individual ? 'E-mail' : 'E-mail do representante',
    contractDate: 'Data do contrato',
    commissionGeneralRate: 'Regra geral (%)',
    commissionApprovedCreditRate: 'Crédito já aprovado (%)',
  };
  return labels[name] ?? name;
}

/**
 * Lista, em português, os campos inválidos e o motivo de cada um ("E-mail do representante: formato
 * inválido"). Vazio quando o formulário pode ser enviado.
 */
export function describeInvalidPartyFields(form: FormGroup): string[] {
  const individual = isIndividualParty(form);
  return Object.entries(form.controls)
    .filter(([, control]) => control.invalid)
    .map(([name, control]) => {
      const label = partyFieldLabel(name, individual);
      const errors = control.errors ?? {};
      if (errors['required']) return `${label}: preencha o campo`;
      if (errors['email']) return `${label}: formato inválido, corrija ou apague`;
      if (errors['maxlength'])
        return `${label}: use no máximo ${errors['maxlength'].requiredLength} caracteres`;
      if (errors['min'] || errors['max'] || errors['pattern'])
        return `${label}: use um número inteiro de 0 a 100`;
      if (errors['documentLength'])
        return `${label}: complete os ${errors['documentLength'].length} dígitos ou apague`;
      return `${label}: valor inválido`;
    });
}

/**
 * Monta o payload dos dados da parte a partir do formulário (strings aparadas, vazio = `null`).
 * Manda só o documento do tipo escolhido: CNPJ na empresa, CPF na pessoa física (o backend recusa
 * os dois juntos).
 */
export function readPartnerContractParty(
  form: FormGroup,
  dateFormatter: DateFormatterService
): PartnerContractPartyRequest {
  const raw = form.getRawValue();
  const individual = raw.partyType === 'Individual';
  const text = (value: unknown): string | null => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    return trimmed ? trimmed : null;
  };
  // A máscara pode devolver o valor formatado; o backend espera só os dígitos.
  const digits = (value: unknown): string | null => text(String(value ?? '').replace(/\D/g, ''));

  return {
    partyLegalName: text(raw.partyLegalName) ?? '',
    partyShortName: text(raw.partyShortName),
    partyCnpj: individual ? null : digits(raw.partyCnpj),
    partyCpf: individual ? digits(raw.partyCpf) : null,
    partyAddress: text(raw.partyAddress),
    partyRepresentativeName: text(raw.partyRepresentativeName),
    partyRepresentativeEmail: text(raw.partyRepresentativeEmail),
    contractDate: dateFormatter.formatDateToYYYYMMDD(raw.contractDate),
    // Vazio = padrão (o `Number('')` viraria 0 em silêncio).
    commissionGeneralRate: percentOrDefault(
      raw.commissionGeneralRate,
      DEFAULT_COMMISSION_GENERAL_RATE
    ),
    commissionApprovedCreditRate: percentOrDefault(
      raw.commissionApprovedCreditRate,
      DEFAULT_COMMISSION_APPROVED_CREDIT_RATE
    ),
  };
}

/** Percentual digitado ou, se vazio, o padrão do Anexo I. */
function percentOrDefault(value: unknown, fallback: number): number {
  if (value === null || value === undefined || String(value).trim() === '') return fallback;
  return Number(value);
}
